import { mistralClient } from "#lib/mistral-client"
import { chatSDK } from "#sdk/chat"
import type { ChatMessage, ChatTool, ChatToolCall } from "@home-finance-manager/shared/types/chat"
import { useCallback, useRef, useState } from "react"
import { useCurrentAccount } from "./use-accounts"

export function useChatAgent() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { data: currentAccount } = useCurrentAccount()
  const abortControllerRef = useRef<AbortController | null>(null)

  const parseToolCallArguments = useCallback((toolCalls: ChatToolCall[]): void => {
    for (const toolCall of toolCalls) {
      if ((toolCall as any)._rawArgs) {
        try {
          toolCall.arguments = JSON.parse((toolCall as any)._rawArgs)
          delete (toolCall as any)._rawArgs
        } catch {
          toolCall.arguments = {}
        }
      }
    }
  }, [])

  const loadTools = useCallback(async (): Promise<ChatTool[]> => {
    try {
      const mcpTools = await chatSDK.mcp.listTools()
      return mcpTools.map((tool) => ({
        type: "function" as const,
        function: {
          name: tool.name,
          description: tool.description,
          parameters: tool.inputSchema,
        },
      }))
    } catch (err) {
      return []
    }
  }, [])

  /**
   * Execute a tool call
   */
  const executeToolCall = useCallback(
    async (toolCall: ChatToolCall): Promise<string> => {
      try {
        const result = await chatSDK.mcp.callTool(
          {
            name: toolCall.name,
            arguments: toolCall.arguments,
          },
          currentAccount?.id ? String(currentAccount.id) : undefined
        )

        if (result.isError) {
          const errorText = result.content?.[0]?.text || "Unknown error"
          return `Error: ${errorText}`
        }

        const resultText = result.content?.[0]?.text
        if (!resultText || resultText.trim() === "") {
          return "Tool executed but returned no data"
        }
        return resultText
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        return `Error executing tool: ${errorMessage}`
      }
    },
    [currentAccount]
  )

  /**
   * Process tool calls from a message
   */
  const processToolCalls = useCallback(
    async (toolCalls: ChatToolCall[]): Promise<ChatMessage[]> => {
      const toolResults: ChatMessage[] = []

      for (const toolCall of toolCalls) {
        const result = await executeToolCall(toolCall)
        toolResults.push({
          id: `tool-result-${toolCall.id}`,
          role: "tool",
          content: result,
          toolCallId: toolCall.id,
          timestamp: new Date().toISOString(),
        })
      }

      return toolResults
    },
    [executeToolCall]
  )

  /**
   * Send a message and get response
   */
  const sendMessage = useCallback(
    async (content: string, stream = true) => {
      if (!mistralClient.isConfigured()) {
        setError(
          "Mistral API is not configured. Please set VITE_MISTRAL_API_KEY environment variable."
        )
        return
      }

      if (!currentAccount) {
        setError("No account selected. Please select an account first.")
        return
      }

      setIsLoading(true)
      setError(null)

      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: "user",
        content,
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, userMessage])

      try {
        const tools = await loadTools()

        const userLocale = navigator.language || "en-US"
        const localeParts = userLocale.split("-")
        const language = localeParts[0] || "en"
        const region = localeParts[1] || "US"

        const currencyMap: Record<string, string> = {
          US: "USD",
          GB: "GBP",
          FR: "EUR",
          DE: "EUR",
          IT: "EUR",
          ES: "EUR",
          NL: "EUR",
          BE: "EUR",
          AT: "EUR",
          PT: "EUR",
          IE: "EUR",
          FI: "EUR",
          CA: "CAD",
          AU: "AUD",
          NZ: "NZD",
          JP: "JPY",
          CN: "CNY",
          CH: "CHF",
          SE: "SEK",
          NO: "NOK",
          DK: "DKK",
        }
        const currency = currencyMap[region] || "EUR"

        const currencyFormatter = new Intl.NumberFormat(userLocale, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        const exampleAmount = currencyFormatter.format(1234.56)

        const languageNames: Record<string, string> = {
          en: "English",
          fr: "French",
          de: "German",
          es: "Spanish",
          it: "Italian",
          pt: "Portuguese",
          nl: "Dutch",
          ja: "Japanese",
          zh: "Chinese",
        }
        const languageName = languageNames[language] || "English"

        const now = new Date()
        const currentYear = now.getFullYear()
        const currentMonth = now.getMonth() + 1
        const currentDate = now.toISOString().split("T")[0]

        const systemMessage: ChatMessage = {
          id: `system-${Date.now()}`,
          role: "system",
          content: `You are a helpful, mindful, and transparent financial assistant for a home finance management application.

IMPORTANT CONTEXT:
- Current date: ${currentDate} (Year: ${currentYear}, Month: ${currentMonth})
- User's locale: ${userLocale} (Language: ${languageName}, Region: ${region})
- Currency: ${currency}
- Currency format example: ${exampleAmount}

CORE PRINCIPLES:
1. Be helpful and proactive: If the user's request is missing important information (like date ranges, categories, filters), ask clarifying questions in a friendly, human-understandable way before making assumptions.
2. Be transparent: If you don't know something or are uncertain, clearly state that. Never make up information or guess.
3. Be mindful: Consider what information would be most useful to the user and ask for it naturally in conversation.

RULES:
1. Time references: When the user asks about time periods (e.g., "this month", "last week", "last year"), always calculate relative to the current date: ${currentDate}
2. Currency formatting: Always format monetary amounts using ${currency}. Example format: ${exampleAmount}
3. Language: Respond in ${languageName} when appropriate, but prioritize clarity and accuracy
4. Date calculations: Use the current date (${currentDate}) as the reference point for all relative time queries
5. Consistency: Always include currency symbols when mentioning amounts

WHEN INFORMATION IS MISSING:
- If the user asks about expenses/income without specifying a time period, ask: "For which time period would you like to see this? For example, this month, last month, or a specific date range?"
- If they ask about categories/partners without specifying which ones, ask: "Would you like to see all categories/partners, or specific ones?"
- If the request is ambiguous, politely ask for clarification: "I want to help you with that. Could you clarify [specific detail]?"
- Always explain why you need the information: "To give you the most accurate results, I need to know..."

WHEN YOU DON'T KNOW:
- Be honest: "I don't have that information available" or "I'm not able to access that data"
- Suggest alternatives: "However, I can help you with [alternative]"
- If a tool call fails or returns unexpected results, explain what happened: "I tried to get that information, but encountered an issue. [Explain the issue]"

When presenting financial data:
- Format all amounts as: ${exampleAmount}
- Use the ${currency} currency code when needed for clarity
- Be precise with numbers and dates
- If data is incomplete or unavailable, mention it clearly`,
          timestamp: new Date().toISOString(),
        }

        const conversationMessages = [systemMessage, ...messages, userMessage]

        if (stream) {
          await handleStreamingResponse(conversationMessages, tools)
        } else {
          await handleNonStreamingResponse(conversationMessages, tools)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to send message"
        setError(errorMessage)
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: `Error: ${errorMessage}`,
            timestamp: new Date().toISOString(),
          },
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [messages, currentAccount, loadTools]
  )

  /**
   * Handle streaming response
   */
  const handleStreamingResponse = useCallback(
    async (conversationMessages: ChatMessage[], tools: ChatTool[]) => {
      const assistantMessageId = `msg-${Date.now()}`
      let assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
      }

      let accumulatedToolCalls: ChatToolCall[] = []
      let toolCallIndex = 0

      setMessages((prev) => [...prev, assistantMessage])

      try {
        for await (const chunk of mistralClient.chatStream({
          messages: conversationMessages,
          tools,
          toolChoice: "auto",
          stream: true,
        })) {
          if (chunk.delta?.content) {
            assistantMessage.content += chunk.delta.content
            setMessages((prev) => {
              const updated = [...prev]
              const msgIndex = updated.findIndex((m) => m.id === assistantMessageId)
              if (msgIndex >= 0) {
                updated[msgIndex] = { ...assistantMessage }
              }
              return updated
            })
          }

          if (chunk.delta?.toolCalls && Array.isArray(chunk.delta.toolCalls)) {
            for (const toolCallDelta of chunk.delta.toolCalls) {
              const index = toolCallDelta.index ?? toolCallIndex

              if (typeof index !== "number" || index < 0) {
                continue
              }

              if (!accumulatedToolCalls[index]) {
                accumulatedToolCalls[index] = {
                  id: toolCallDelta.id || `tool-call-${index}`,
                  name: toolCallDelta.name || "",
                  arguments: {} as any,
                }
                ;(accumulatedToolCalls[index] as any)._rawArgs = ""
              }

              const toolCall = accumulatedToolCalls[index]

              if (toolCallDelta.name) {
                toolCall.name = toolCallDelta.name
              }

              if (toolCallDelta.arguments) {
                const rawArgs = (toolCall as any)._rawArgs || ""
                const combined = rawArgs + toolCallDelta.arguments
                ;(toolCall as any)._rawArgs = combined

                try {
                  const parsed = JSON.parse(combined)
                  toolCall.arguments = parsed
                  delete (toolCall as any)._rawArgs
                } catch {
                  // Partial JSON, continue accumulating
                }
              }

              assistantMessage.toolCalls = [...accumulatedToolCalls]
              setMessages((prev) => {
                const updated = [...prev]
                const msgIndex = updated.findIndex((m) => m.id === assistantMessageId)
                if (msgIndex >= 0) {
                  updated[msgIndex] = { ...assistantMessage }
                }
                return updated
              })
            }
          }

          if (chunk.finishReason === "tool_calls" && accumulatedToolCalls.length > 0) {
            parseToolCallArguments(accumulatedToolCalls)
            const toolResults = await processToolCalls(accumulatedToolCalls)
            setMessages((prev) => [...prev, ...toolResults])

            const followUpMessages = [...conversationMessages, assistantMessage, ...toolResults]
            await handleStreamingResponse(followUpMessages, tools)
            return
          }
        }

        if (accumulatedToolCalls.length > 0 && assistantMessage.toolCalls) {
          parseToolCallArguments(accumulatedToolCalls)
          const toolResults = await processToolCalls(accumulatedToolCalls)
          setMessages((prev) => [...prev, ...toolResults])

          const followUpMessages = [...conversationMessages, assistantMessage, ...toolResults]
          await handleStreamingResponse(followUpMessages, tools)
        }
      } catch (err) {
        throw err
      }
    },
    [processToolCalls]
  )

  /**
   * Handle non-streaming response
   */
  const handleNonStreamingResponse = useCallback(
    async (conversationMessages: ChatMessage[], tools: ChatTool[]) => {
      const response = await mistralClient.chat({
        messages: conversationMessages,
        tools,
        toolChoice: "auto",
        stream: false,
      })

      const assistantMessage: ChatMessage = {
        ...response.message,
        toolCalls: response.message.toolCalls,
      }

      setMessages((prev) => [...prev, assistantMessage])

      if (assistantMessage.toolCalls && assistantMessage.toolCalls.length > 0) {
        const toolResults = await processToolCalls(assistantMessage.toolCalls)
        setMessages((prev) => [...prev, ...toolResults])

        const followUpMessages = [...conversationMessages, assistantMessage, ...toolResults]
        await handleNonStreamingResponse(followUpMessages, tools)
      }
    },
    [processToolCalls]
  )

  /**
   * Clear messages
   */
  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  /**
   * Cancel ongoing request
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsLoading(false)
  }, [])

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    cancel,
  }
}
